import type {
  Budgets,
  ProviderId,
  SetBudgetsRequest,
  ProviderStatus,
  SetWorkloadProviderRequest,
  UsageSummary,
  WorkloadRoutingStatus,
} from "./types.js";

/**
 * Typed fetch wrappers for the dashboard's /api/ai-providers proxy.
 * Threads the Authorization header from the calling component, mirroring
 * the Otto Bots panel's api.ts.
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
  authHeader?: string,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (authHeader) headers.set("Authorization", authHeader);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`/api/ai-providers${path}`, { ...init, headers });
  const text = await res.text();
  if (!res.ok) {
    let body: unknown;
    try { body = JSON.parse(text); } catch { body = text; }
    // The dashboard proxy surfaces `{error}`; the gateway surfaces
    // `{detail}` (e.g. 409 missing-key). Prefer whichever is present so
    // the panel never shows a raw "[object Object]".
    //
    // `detail` is sometimes an OBJECT rather than a string — the
    // per-workload 422 sends `{error, provider, workload, missing, message}`
    // so the UI can explain WHY a provider was refused. Stringifying that
    // whole object is exactly the "[object Object]" this block exists to
    // avoid, so unwrap its human-readable `message` first.
    const asRecord =
      typeof body === "object" && body !== null
        ? (body as Record<string, unknown>)
        : null;
    const detail = asRecord?.["detail"];
    const nestedMessage =
      typeof detail === "object" && detail !== null
        ? (detail as Record<string, unknown>)["message"] ??
          (detail as Record<string, unknown>)["detail"]
        : undefined;

    const msg =
      asRecord && "error" in asRecord
        ? String(asRecord["error"])
        : nestedMessage !== undefined
          ? String(nestedMessage)
          : detail !== undefined
            ? String(detail)
            : `Request failed: ${res.status}`;
    throw new Error(msg);
  }
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const aiProvidersApi = {
  status: (auth?: string) => request<ProviderStatus>("", {}, auth),
  setActive: (provider: ProviderId, auth?: string) =>
    request<ProviderStatus>("", { method: "PUT", body: JSON.stringify({ provider }) }, auth),
  /** Pick a provider's default model; `null` returns it to the boot default. */
  setModel: (provider: ProviderId, model: string | null, auth?: string) =>
    request<ProviderStatus>(
      `/${provider}/model`,
      { method: "PUT", body: JSON.stringify({ model }) },
      auth,
    ),
  /** The AI spend budgets (daily sales, daily marketing, monthly). */
  budgets: (auth?: string) => request<Budgets>("/budgets", {}, auth),
  setBudgets: (body: SetBudgetsRequest, auth?: string) =>
    request<Budgets>("/budgets", { method: "PUT", body: JSON.stringify(body) }, auth),
  /** Month-to-date LLM spend summary (default: current month). */
  usage: (month?: string, auth?: string) =>
    request<UsageSummary>(
      month ? `/usage?month=${encodeURIComponent(month)}` : "/usage",
      {},
      auth,
    ),
  /** Per-feature provider routing — the table under the global switch. */
  workloads: (auth?: string) =>
    request<WorkloadRoutingStatus>("/workloads", {}, auth),
  /**
   * Set or clear one assignment. `provider: null` clears the override so
   * the row inherits from its group (or the global default) again.
   * Returns the whole refreshed table, so the caller never has to guess
   * how an override rippled through the rows that inherit from it.
   */
  setWorkload: (body: SetWorkloadProviderRequest, auth?: string) =>
    request<WorkloadRoutingStatus>(
      "/workloads",
      { method: "PUT", body: JSON.stringify(body) },
      auth,
    ),
};
